import { Box } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  targetDate: string;
}

function getMsRemaining(targetDate: string): number {
  return DateTime.fromISO(targetDate, { zone: 'utc' }).toMillis() - DateTime.utc().toMillis();
}

function getColor(msRemaining: number): 'success' | 'warning-500' | 'danger' {
  const hours = msRemaining / (1000 * 60 * 60);
  if (hours > 24) return 'success';
  if (hours > 12) return 'warning-500';
  return 'danger';
}

function formatCountdown(msRemaining: number): string {
  if (msRemaining <= 0) return 'Overdue';
  const totalMinutes = Math.floor(msRemaining / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
}

export const ReviewSlaCountdown = ({ targetDate }: Props) => {
  const [msRemaining, setMsRemaining] = useState(() => getMsRemaining(targetDate));

  const tick = useCallback(() => {
    setMsRemaining(getMsRemaining(targetDate));
  }, [targetDate]);

  useEffect(() => {
    tick();
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [tick]);

  const formattedTarget = DateTime.fromISO(targetDate, { zone: 'utc' }).toLocal().toFormat('MMM d, h:mm a ZZZZ');

  return (
    <Box display="flex" direction="column" alignItems="flex-end" gap="xs">
      <Box fontSize="sm" color="body-secondary">
        Target: {formattedTarget}
      </Box>
      <Box fontSize="sm" fontWeight="bold" color={getColor(msRemaining)}>
        {formatCountdown(msRemaining)}
      </Box>
    </Box>
  );
};
